import { Divider, Input, message, Modal, Select, Spin, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import './AddAccountFromUserHubs.less';
import { CloseIcon, ErrorIcon, NewCloseIcon, NewSaveIcon } from '../../Common/Icons';
import { useSelector } from 'react-redux';
import { isTimeZoneValid } from '../../Common/TimezoneUtils';
import { selectProviderData } from '@vantiva-sa/homesight_humaui/Store/providerDataStore';
import { Units } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';

const AddAccountFromUserHubs = ({
  isAddAccountModalOpen,
  setIsAddAccountModalOpen,
  onActionClick,
}) => {
  const trans = useSelector(translator);
  const providerData = useSelector(selectProviderData);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameFieldError, setNameFieldError] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [isTimeFormatEnabled, setIsTimeFormatEnabled] = useState(
    providerData?.is24hoursformat || false
  );
  const [accountType, setAccountType] = useState('');
  const [showTimezoneWarning, setShowTimezoneWarning] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  // Get provider config

  // Default fallback locale/timezone
  const defaultLocaleVal = providerData?.locale || 'en-US';
  const defaultTimezoneVal = providerData?.timezone || 'America/Los_Angeles (PDT)';
  const defaultMeasurementUnit = providerData?.measurement || 'imperial';
  const [measurementUnit, setMeasurementUnit] = useState(defaultMeasurementUnit);
  const [selectedLocale, setSelectedLocale] = useState(defaultLocaleVal);
  const [selectedTimezone, setSelectedTimezone] = useState(defaultTimezoneVal);
  const [isProcessing, setIsProcessing] = useState(false);
  const userApi = useSelector((state) => state.apis.userApi);

  // Timezone combos
  const getTimezoneOptions = () => {
    const localeVal = selectedLocale;
    const timezonesMap = providerData?.supportedtimezones.find((tz) => tz[localeVal]) || {};
    const timezones = timezonesMap[localeVal] || [];
    return timezones
      .filter((tz) => isTimeZoneValid(tz.split(' ')[0]))
      .map((tz) => {
        const match = tz.match(/^(.+?)\s*\((.*?)\)?$/);
        if (match) {
          const [, zone, abbrev] = match;
          return {
            label: abbrev ? `${zone} (${abbrev})` : zone,
            value: tz,
          };
        } else {
          return {
            label: tz,
            value: tz,
          };
        }
      });
  };
  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setNameFieldError('');
    setPostalCode('');
    setMeasurementUnit(defaultMeasurementUnit);
    setSelectedLocale(defaultLocaleVal);
    setSelectedTimezone(defaultTimezoneVal);
    setIsTimeFormatEnabled(false);
    setAccountType('');
    setShowTimezoneWarning(false);
  };
  const handleCloseAddAccountModal = () => {
    resetForm();
    setIsAddAccountModalOpen(false);
  };

  const handleAddAccountHandler = () => {
    if (!selectedTimezone) {
      setShowTimezoneWarning(true);
      return;
    }
    const errorCallback = (error) => {
      setIsProcessing(false);
      console.error('Error Creating Account', error);
      setNameFieldError(error?.info || trans('ERROR_CREATING_ACCOUNT'));
    };
    const successCallback = (data) => {
      setIsProcessing(false);
      // message.success("Account Created");
      onActionClick(null, null, true, true)();
      resetForm();
      setIsAddAccountModalOpen(false);
    };
    try {
      setIsProcessing(true);
      const payload = {
        rolename: 'Principal',
        firstname: firstName,
        lastname: lastName,
        postalcode: postalCode,
        measurement: measurementUnit,
        locale: selectedLocale,
        timezone: selectedTimezone,
        is24hoursformat: isTimeFormatEnabled,
        deployment_state: accountType,
      };
      userApi.createAccount(
        payload,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (e) {
      errorCallback(e);
    }
  };

  useEffect(() => {
    const isValid =
      firstName.trim() !== '' &&
      lastName.trim() !== '' &&
      selectedLocale &&
      measurementUnit &&
      selectedTimezone &&
      accountType;
    setIsSubmitDisabled(!isValid);
  }, [firstName, lastName, selectedLocale, measurementUnit, selectedTimezone, accountType]);

  useEffect(() => {
    setSelectedLocale(providerData?.locale || 'en-US');
    setSelectedTimezone(providerData?.timezone || 'America/Los_Angeles (PDT)');
    setIsTimeFormatEnabled(providerData?.is24hoursformat || false);
    setMeasurementUnit(providerData?.measurement || null);
  }, [providerData]);

  return (
    <Modal
      open={isAddAccountModalOpen}
      onCancel={handleCloseAddAccountModal}
      footer={null}
      closable={false}
      centered
      className="user-hubs-add-account-main-modal"
    >
      <div className="user-hubs-add-account-main-container">
        <Spin size="large" spinning={isProcessing} tip={trans('PROCESSING')}>
          <div className="user-hubs-add-account-header-container">
            <div className="user-hubs-add-account-label-container">
              <div className="user-hubs-add-account-label">{trans('ADD_ACCOUNT_L')}</div>
            </div>
            <CloseIcon
              className="user-hubs-add-account-close-icon"
              onClick={() => handleCloseAddAccountModal()}
            />
          </div>
          <div className="user-hubs-add-account-content-container">
            <div className="user-hubs-add-account-content-column">
              <span className="user-hubs-add-account-content-column-label">
                {trans('WHOSACCOUNTISTHIS?')}
              </span>
              <div className="user-hubs-add-account-content-form-item user-hubs-add-account-content-form-item-name">
                <div className="user-hubs-add-account-form-item-row">
                  <span className="user-hubs-add-account-row-form-item-label">
                    {trans('FIRST_NAME*_L')}
                  </span>
                  <div className="user-hubs-add-account-form-item-input">
                    <Input
                      className="user-hubs-add-account-form-item-input-box"
                      value={firstName}
                      onChange={(e) => {
                        setNameFieldError('');
                        setFirstName(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="user-hubs-add-account-form-item-row">
                  <span className="user-hubs-add-account-row-form-item-label">
                    {trans('LAST_NAME*_L')}
                  </span>
                  <div className="user-hubs-add-account-form-item-input">
                    <Input
                      className="user-hubs-add-account-form-item-input-box"
                      value={lastName}
                      onChange={(e) => {
                        setNameFieldError('');
                        setLastName(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="user-hubs-add-account-form-item-row">
                  <span className="user-hubs-add-account-row-form-item-label">
                    {trans('ACCOUNTTYPE_REQUIRED')}
                  </span>
                  <div className="user-hubs-add-account-form-item-input">
                    <Select
                      className="user-hubs-add-account-form-item-select-box"
                      dropdownClassName="account-type-dropdown-menu"
                      value={accountType || undefined}
                      onChange={(value) => setAccountType(value)}
                      placeholder={trans('SELECT_ACCOUNT_TYPE')}
                      options={[
                        { value: 'DEPLOYED', label: trans('CLIENT') },
                        { value: 'NOT_DEPLOYED', label: trans('DEMO') },
                      ]}
                    />
                  </div>
                </div>
                {nameFieldError && (
                  <div className="user-hubs-name-field-error">{nameFieldError}</div>
                )}
              </div>
            </div>
            <Divider className="user-hubs-add-account-divider" type="horizontal" />
            <div className="user-hubs-add-account-content-column">
              <span className="user-hubs-add-account-content-column-label">
                {trans('GENERAL_INFORMATION_L')}
              </span>
              {/* <div className="user-hubs-add-account-content-form-2"></div> */}
              <div className="user-hubs-add-account-content-form-item">
                <div className="user-hubs-add-account-form-item-row">
                  <span className="user-hubs-add-account-row-form-item-label">
                    {trans('LANGUAGE*')}
                  </span>
                  <div className="user-hubs-add-account-form-item-input">
                    <Select
                      className="user-hubs-add-account-form-item-select-box"
                      value={selectedLocale}
                      onChange={(val) => {
                        setSelectedLocale(val);
                        setSelectedTimezone('');
                        setShowTimezoneWarning(true);
                      }}
                      placeholder={trans('SELECT_LANGUAGE_L')}
                      options={
                        providerData &&
                        providerData.supportedlocales
                          ?.map((item) => ({
                            value: item.slice(-6, -1),
                            label: item,
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label))
                      }
                    />
                  </div>
                </div>
                <div className="user-hubs-add-account-form-item-row">
                  <span className="user-hubs-add-account-row-form-item-label">
                    {trans('MEASUREMENT_UNIT*')}
                  </span>
                  <div className="user-hubs-add-account-form-item-input">
                    <Select
                      className="user-hubs-add-account-form-item-select-box"
                      value={measurementUnit}
                      onChange={(val) => setMeasurementUnit(val)}
                      placeholder={trans('SELECT_UNIT_L')}
                      options={Units.map((unit) => ({
                        label: `${unit.label}`,
                        value: `${unit.value}`,
                      }))}
                    />
                  </div>
                </div>
              </div>
              <div className="user-hubs-add-account-content-form-item">
                <div className="user-hubs-add-account-form-item-row">
                  <span className="user-hubs-add-account-row-form-item-label">
                    {trans('POSTALCODEOPTIONAL')}
                  </span>
                  <div className="user-hubs-add-account-form-item-input">
                    <Input
                      className="user-hubs-add-account-form-item-input-box"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder={trans('ENTER_POSTALCODE_L')}
                    />
                  </div>
                  <div className="user-hubs-postal-code-info-container">
                    <div className="user-hubs-postal-code-info-message">
                      <ErrorIcon className="user-hubs-error-icon" fillColor="#51b4f6" />
                      <span className="user-hubs-postal-code-info-message-label">
                        {trans('WENEEDTHISINFORMATIONTOPROVIDETHEWEATHERFORECAST')}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className="user-hubs-add-account-form-item-row"
                  style={{ marginTop: showTimezoneWarning ? '1.5rem' : '' }}
                >
                  <span className="user-hubs-add-account-row-form-item-label">
                    {trans('TIME*')}
                  </span>
                  <div
                    className={`user-hubs-add-account-form-item-input ${
                      showTimezoneWarning ? 'user-hubs-add-account-form-item-input-error' : ''
                    }`}
                  >
                    <Select
                      className="user-hubs-add-account-form-item-select-box"
                      value={selectedTimezone}
                      onChange={(val) => {
                        setSelectedTimezone(val);
                        setShowTimezoneWarning(false);
                      }}
                      placeholder={trans('SELECT_TIMEZONE_L')}
                      options={getTimezoneOptions()}
                    />
                  </div>
                  {showTimezoneWarning && (
                    <div className="user-hubs-timezone-warning">
                      <ErrorIcon className="user-hubs-timezone-error-icon" fillColor="#F46363" />
                      <span className="user-hubs-timezone-warning-text">
                        {trans('PLEASESELECTHETIMEZONESELECTION')}
                      </span>
                    </div>
                  )}
                  <div className="user-hubs-add-account-time-format-container">
                    <div className="user-hubs-add-account-timeformat-switch">
                      <Switch
                        checked={!!isTimeFormatEnabled}
                        onChange={(checked) => {
                          setIsTimeFormatEnabled(!!checked);
                        }}
                      />
                    </div>
                    <span className="user-hubs-add-account-time-format-label">
                      {trans('24HOURFORMAT_L')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="user-hubs-add-account-mandatory-label">
            {trans('*THESEFILEDSAREMANADATORYTOCREATEANACCOUNT')}
          </div>
          <div className="user-hubs-add-account-footer-container">
            <div
              className="user-hubs-add-account-footer-cancel"
              onClick={() => handleCloseAddAccountModal()}
            >
              <div className="user-hubs-add-account-footer-cancel-block">
                <NewCloseIcon className="user-hubs-add-account-cancel-icon" />
                <div className="user-hubs-add-account-cancel">
                  <span className="user-hubs-add-account-cancel-label">{trans('CANCEL')}</span>
                </div>
              </div>
            </div>
            <div
              className={`user-hubs-add-account-footer-add-account ${
                isSubmitDisabled ? 'user-hubs-add-account-footer-add-account-disabled' : ''
              }`}
              onClick={() => {
                if (!isSubmitDisabled) handleAddAccountHandler();
              }}
            >
              <div className="user-hubs-add-account-footer-save-block">
                <NewSaveIcon
                  className={`user-hubs-add-account-save-icon ${
                    isSubmitDisabled ? 'user-hubs-add-account-save-icon-disabled' : ''
                  }`}
                />
                <div className="user-hubs-add-account-save">
                  <span
                    className={`user-hubs-add-account-save-label ${
                      isSubmitDisabled ? 'user-hubs-add-account-save-label-disabled' : ''
                    }`}
                  >
                    {trans('ADD_NEWACCOUNT_L')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Spin>
      </div>
    </Modal>
  );
};
export default AddAccountFromUserHubs;
